import toast from "@pro/js/components/ui/toast";
import useAccordionRegistry from "./use-accordion-registry";
import _ from "lodash";

/**
 * Recursively flattens a nested errors object into an array of string paths.
 */
function flattenErrors(obj, prefix = "") {
  let result = [];

  _.forOwn(obj, (value, key) => {
    const path = prefix
      ? Array.isArray(obj)
        ? `${prefix}[${key}]`
        : `${prefix}[${key}]`
      : key;

    if (_.isPlainObject(value) || _.isArray(value)) {
      result = result.concat(flattenErrors(value, path));
    } else if (value != null && value !== "") {
      result.push(path);
    }
  });

  return result;
}

/**
 * Custom hook to open accordions that contain erroring fields.
 * It also scrolls to the first accordion that contains an error and
 * focuses its first erroring field.
 * focusErroringField(errors) takes the formik errors object as argument.
 *
 * @returns {Function} - focusErroringField(errors)
 */
const useFocusErroringField = () => {
  const { getRegistry } = useAccordionRegistry();

  const focusErroringField = (errors) => {
    /**
     * First, open all accordions that contain erroring fields.
     * Therefore, we need to check the registry for fields that match the error keys.
     */
    const registry = getRegistry();
    const flattenedErrors = flattenErrors(errors);

    if (!flattenedErrors.length) return; // no errors, nothing to do

    toast({
      type: "error",
      message: "Please check the form for errors.",
    });

    // collect erroring accordion items in an array
    const sectionsToOpen = [];
    for (const [sectionId, meta] of Object.entries(registry)) {
      if (meta.fields.some((field) => flattenedErrors.some(err => err.includes(field)))) {
        sectionsToOpen.push(sectionId);
      }
    }

    sectionsToOpen.forEach((id) => {
      let accordion = document.getElementById(id);
      if (accordion === null) return; // accordion not found

      let button = accordion.querySelector(".accordion__button");

      // if aria-expanded on the button is false, the accordion is closed
      if (button.getAttribute("aria-expanded") === "false") {
        // emulate the click on the accordion button to open it
        button.click();
      }
    });

    /**
     * When erroring accordions are opened, focus the first erroring field and scroll to it.
     */
    if (flattenedErrors.length) {
      const firstErrorField = document.getElementById(flattenedErrors[0]);

      if (firstErrorField) {
        setTimeout(() => {
          firstErrorField.focus();
          firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 800);
      }
    }

  };

  return focusErroringField;
};

export default useFocusErroringField;
